import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import {
  getFurnishingNumberOfRooms,
  NumberOfRoomsOption,
  getHomeTypes,
  HomeTypeOption
} from './furnishing.service';
import { isErrorInfo } from '../shared/interfaces/error-info.interface';

interface FurnishingUnitTypesContextValue {
  unitTypeOptions: NumberOfRoomsOption[];
  loadingUnitTypes: boolean;
  homeTypeOptions: HomeTypeOption[];
  loadingHomeTypes: boolean;
  error: string | null;
  refetch: () => void;
}

const FurnishingUnitTypesContext = createContext<FurnishingUnitTypesContextValue | undefined>(
  undefined
);

interface FurnishingUnitTypesProviderProps {
  children: React.ReactNode;
}

export const FurnishingUnitTypesProvider: React.FC<FurnishingUnitTypesProviderProps> = ({
  children
}) => {
  const [unitTypeOptions, setUnitTypeOptions] = useState<NumberOfRoomsOption[]>([]);
  const [loadingUnitTypes, setLoadingUnitTypes] = useState<boolean>(false);
  const [homeTypeOptions, setHomeTypeOptions] = useState<HomeTypeOption[]>([]);
  const [loadingHomeTypes, setLoadingHomeTypes] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUnitTypes = useCallback(() => {
    setLoadingUnitTypes(true);
    setError(null);
    getFurnishingNumberOfRooms()
      .then((response) => {
        if (!isErrorInfo(response)) {
          setUnitTypeOptions(response as NumberOfRoomsOption[]);
        } else {
          setError('Failed to fetch unit types');
        }
      })
      .finally(() => {
        setLoadingUnitTypes(false);
      });
  }, []);

  const fetchHomeTypes = useCallback(() => {
    setLoadingHomeTypes(true);
    getHomeTypes()
      .then((response) => {
        if (!isErrorInfo(response)) {
          setHomeTypeOptions(response as HomeTypeOption[]);
        } else {
          setError('Failed to fetch home types');
        }
      })
      .finally(() => {
        setLoadingHomeTypes(false);
      });
  }, []);

  useEffect(() => {
    fetchUnitTypes();
    fetchHomeTypes();
  }, []);

  const refetch = useCallback(() => {
    fetchUnitTypes();
    fetchHomeTypes();
  }, [fetchUnitTypes, fetchHomeTypes]);

  const value = useMemo(
    () => ({
      unitTypeOptions,
      loadingUnitTypes,
      homeTypeOptions,
      loadingHomeTypes,
      error,
      refetch
    }),
    [unitTypeOptions, loadingUnitTypes, homeTypeOptions, loadingHomeTypes, error, refetch]
  );

  return (
    <FurnishingUnitTypesContext.Provider value={value}>
      {children}
    </FurnishingUnitTypesContext.Provider>
  );
};

export const useFurnishingUnitTypes = (): FurnishingUnitTypesContextValue => {
  const context = useContext(FurnishingUnitTypesContext);
  if (context === undefined) {
    throw new Error('useFurnishingUnitTypes must be used within a FurnishingUnitTypesProvider');
  }
  return context;
};
